// After paying for a feis registration (Stripe returns here with
// ?payment_intent=…), or straight away when nothing was owed (&free=1, which
// is checked against the family's registrations before it says so).
// The old page said "Congratulations!" whatever happened and lost the event
// name; this one asks the server for the payment's status first and shows
// the event and who was entered (saved by RegisterPage before paying).
// The registration itself is made by the payment webhook, so a successful
// payment can take a moment to show on Feis.link.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import ParentHeader from "../ParentHeader/ParentHeader";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import { formatDate } from "../../helper/formatDate";
import { Icon, SummarySections } from "./parts";
import { removeFromDraft } from "./registrationModel";
import { successStoreKey } from "./RegisterPage";
import "../../Teacher/Oireachtas/oireachtas.css";
import "./register.css";

const readSaved = (eventId, paymentId) => {
  try {
    return JSON.parse(sessionStorage.getItem(successStoreKey(eventId, paymentId)) || "null");
  } catch (_) {
    return null;
  }
};

// One check per link: a new ?id=/?payment_intent= remounts the page, so a
// verdict for one payment never shows (or clears a draft) for another.
const RegisterSuccess = ({ edit }) => {
  const { search } = useLocation();
  return <SuccessFor key={search} edit={edit} />;
};

const SuccessFor = ({ edit }) => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const eventId = params.get("id");
  const intentId = params.get("payment_intent");
  const free = params.get("free") === "1";
  const regId = params.get("reg");
  const saveToken = params.get("save");
  const saved = eventId && (free ? saveToken : intentId) ? readSaved(eventId, free ? saveToken : intentId) : null;
  // "checking" | "succeeded" | "processing" | "failed"
  const [status, setStatus] = useState(free || intentId ? "checking" : "failed");

  useEffect(() => {
    document.title = "Feis.link - Registration";
    if (free && edit) {
      // A change with nothing to pay was saved at once: believe it when this
      // child's entry (same registration, same dancer) now holds exactly the
      // dances that were saved.
      let cancelled = false;
      const [dancerId, want] = Object.entries(saved?.paid || {})[0] || [];
      const sameSet = (a, b) => a.length === b.length && [...a].map(String).sort().every((id, i) => id === [...b].map(String).sort()[i]);
      (async () => {
        try {
          if (!dancerId || !Array.isArray(want)) return setStatus("processing");
          const res = await serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/registration/regdetails/${regId}`);
          const reg = res?.result?.[0];
          if (reg) {
            const ok = String(reg._id) === String(regId) && String(reg.dancer_id) === String(dancerId) && sameSet(reg.competitions || [], want);
            if (!cancelled) setStatus(ok ? "succeeded" : "processing");
            return;
          }
          // An entry with every dance removed has no details to read; it
          // still exists (the dancer shows as entered with this registration).
          if (want.length === 0) {
            const list = await serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/account/dancers`, { feisId: eventId });
            const d = (list?.dancers || []).find((x) => String(x._id) === String(dancerId));
            if (!cancelled) setStatus(d && String(d.registrationId) === String(regId) ? "succeeded" : "processing");
            return;
          }
          if (!cancelled) setStatus("processing");
        } catch (_) {
          if (!cancelled) setStatus("processing");
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (free) {
      // Nothing to pay: the checkout registered them already. Believe it only
      // when every dancer saved before checkout is now entered.
      let cancelled = false;
      const ids = (saved?.dancers || []).map((d) => d.id).filter(Boolean);
      serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/account/dancers`, { feisId: eventId })
        .then((res) => {
          if (cancelled) return;
          const entered = new Set((res?.dancers || []).filter((d) => d.isRegistered).map((d) => String(d._id)));
          setStatus(ids.length && ids.every((id) => entered.has(id)) ? "succeeded" : "processing");
        })
        .catch(() => !cancelled && setStatus("processing"));
      return () => {
        cancelled = true;
      };
    }
    if (!intentId) return undefined;
    let cancelled = false;
    let tries = 0;
    const check = async () => {
      tries += 1;
      try {
        const res = await serviceConsumer("POST", `${process.env.REACT_APP_BASE_URL}/stripe/confirmpayment`, { paymentId: intentId });
        const s = res?.response?.status;
        if (cancelled) return;
        if (s === "succeeded") return setStatus("succeeded");
        if (s === "processing" || s === "requires_capture") {
          setStatus("processing");
          if (tries < 6) setTimeout(check, 2500);
          return;
        }
        setStatus("failed");
      } catch (_) {
        if (cancelled) return;
        if (tries < 3) setTimeout(check, 2000);
        else setStatus("processing"); // can't tell: say so, never "failed"
      }
    };
    check();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [free, intentId, eventId, regId, edit, saveToken]);

  useEffect(() => {
    // only the picks this payment covered; anything picked since stays
    if (status === "succeeded" && eventId && !edit && saved?.paid) removeFromDraft(eventId, saved.paid);
  }, [status, eventId, edit]);

  const date = saved?.date ? moment(formatDate(saved.date)).format("ddd MMM D") : "";
  const back = () => (eventId ? navigate(`/register-dancers?id=${eventId}`) : navigate("/parent"));

  return (
    <div className="oir rg">
      <ParentHeader />
      <div className="rg-ok">
        {status === "checking" && (
          <>
            <div className="ring wait"><Icon name="hourglass_top" /></div>
            <h2>Checking your payment…</h2>
          </>
        )}
        {status === "processing" && (
          <>
            <div className="ring wait"><Icon name="hourglass_top" /></div>
            <h2>{free ? "Almost there" : "Payment processing"}</h2>
            <p>
              {free && edit
                ? "We couldn't confirm the change yet. Check your entries on Feis.link in a minute."
                : free
                ? "We couldn't confirm the entry yet. Check your entries on Feis.link in a minute; if it isn't there, register again."
                : "Your bank is still confirming the payment. You'll get an email receipt once it goes through, and the entry will show on Feis.link."}
            </p>
            <button type="button" className="rg-cta ghost" style={{ marginTop: 18 }} onClick={() => navigate("/parent")}>View your entries</button>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="ring bad"><Icon name="error" /></div>
            <h2>The payment didn't go through</h2>
            <p>Nothing was charged and your picks are still saved. Please try again.</p>
            <button type="button" className="rg-cta" style={{ marginTop: 18 }} onClick={back}>Back to registration</button>
          </>
        )}
        {status === "succeeded" && (
          <>
            <div className="ring"><Icon name="check_circle" /></div>
            <h2>{edit ? "Entry updated!" : "You're entered!"}</h2>
            {saved?.name && <p><b>{saved.name}</b>{date ? ` · ${date}` : ""}</p>}
            {!free && <p>A receipt is on its way to your email.</p>}
            {saved?.dancers?.length > 0 && (
              <div className="rg-card">
                {/* each child's dances in one list, as on the review: by level, one per
                    line, a 2nd chance under its dance (older saves: their lines, one each) */}
                {saved.dancers.map((d, i) => (
                  <div className="rg-ok-d" key={i}>
                    <div className="rg-ok-name">{d.fname}</div>
                    {Array.isArray(d.sections) ? (
                      <SummarySections sections={d.sections} prices={false} />
                    ) : (
                      <ul className="rg-ok-lines">
                        {(d.lines || []).map((l, j) => (
                          <li key={j}>{l}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="rg-note green">
              <Icon name="confirmation_number" />
              <div>Number cards and the schedule will be on Feis.link closer to the day. Your entry can take a minute to show up.</div>
            </div>
            <button type="button" className="rg-cta" style={{ marginBottom: 10 }} onClick={() => navigate("/parent")}>View your entries</button>
          </>
        )}
      </div>
    </div>
  );
};

export default RegisterSuccess;
